import React from 'react';
import classNames from 'classnames';
import { Link, useHistory } from 'umi';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Menu } from '@zbanx-coding/components-basic';
import { CaretDownFilled } from '@zbanx/icons';
import { useAppContext } from '@/hooks/useAppContext';
import { IMAGE_LOGO } from '@/bizComponents/images/logo';
import { LoadingOutlined } from '@ant-design/icons';
import { useLogout } from '@/hooks/useLogout';
import css from './index.less';
import { partnerAppUrl } from '@/utils/url/partnerAppUrl';
import { merchantAppUrl } from '@/utils/url/merchantAppUrl';
import { AccountRelation } from '@/entities/AccountRelation';
import { setDefaultRelation } from '@/services/account/setDefaultRelation';
import { getAppSettings } from '@/shared/getAppSettings';
import { getCurrentPathname } from '@/utils/getCurrentPathname';
import { setRelationType } from '@/persistence/cookie';
import { transformToWebp } from '@/utils/image';

export interface Props {
  style?: React.CSSProperties;
  className?: string;
}

export const Header: React.FC<Props> = ({ style, className }) => {
  const { t } = useTranslation();
  const { account, relations } = useAppContext();
  const history = useHistory();
  const [menuVisible, setMenuVisible] = React.useState(false);
  const { loggingOut, handleLogout } = useLogout();
  const [changing, setChanging] = React.useState(false);
  const handleClickLogout = React.useCallback(() => {
    handleLogout().then(() => {
      if (getCurrentPathname() !== '/') {
        window.location.reload();
      }
    });
  }, [handleLogout]);

  const avatarText = React.useMemo(() => {
    if (account && !account.avatar && account.email) {
      return account.email.slice(0, 1).toUpperCase();
    }
    return null;
  }, [account]);

  // 合作者角色或商家角色
  const { merchantRole, partnerRole, bankerRole } = React.useMemo(() => {
    let partner: AccountRelation | boolean = false;
    let merchant: AccountRelation | boolean = false;
    let banker: AccountRelation | boolean = false;

    if (relations) {
      for (const relation of relations) {
        if (relation.type === 1) {
          partner = relation;
        } else if (relation.type === 2) {
          merchant = relation;
        } else if (relation.type === 3) {
          banker = relation;
        }
      }
    }

    if (partner === false && merchant === false && banker === false) {
      if (account?.relation === 'merchant') {
        merchant = true;
      } else {
        partner = true;
      }
    }

    return { partnerRole: partner, merchantRole: merchant, bankerRole: banker };
  }, [relations, account]);

  const hasMerchantRole = !!merchantRole;
  const hasPartnerRole = !!partnerRole;
  const hasBankerRole = !!bankerRole;

  const handleClickMerchant = React.useCallback(() => {
    setChanging(true);
    setRelationType('merchant');

    if (typeof merchantRole === 'boolean') {
      location.href = merchantAppUrl('/merchant/homePage');
    } else {
      setDefaultRelation(merchantRole.id).then(() => {
        location.href = merchantAppUrl('/merchant/homePage');
      });
    }
  }, [merchantRole]);

  const handleClickPartner = React.useCallback(() => {
    setChanging(true);
    setRelationType('partner');

    if (typeof partnerRole === 'boolean') {
      location.href = partnerAppUrl('/user/homePage');
    } else {
      setDefaultRelation(partnerRole.id).then(() => {
        location.href = partnerAppUrl('/user/homePage');
      });
    }
  }, [partnerRole]);

  const handleClickBanker = React.useCallback(() => {
    setChanging(true);
    location.href = getAppSettings().bankerSiteDomain;
  }, []);

  return (
    <header
      id="page-header"
      className={classNames(css.header, className)}
      style={style}
    >
      <div className={css.container}>
        <div className={css.logo}>
          <Link to="/" title={t('biz.app.header.homePage.link.title')}>
            <img
              src={transformToWebp(IMAGE_LOGO)}
              alt={t('shared.logo.img.alt')}
            />
          </Link>
        </div>
        <div className={css.actions}>
          {account ? (
            <div className={css.userBox}>
              {changing ? (
                <div className={css.switching}>
                  <span className={css.switchingText}>
                    {t('biz.user.menu.to.switching')}
                  </span>
                  <LoadingOutlined />
                </div>
              ) : (
                <div className={css.user}>
                  <div className={css.avatar}>
                    {avatarText ? (
                      <div className={css.textAvatar}>{avatarText}</div>
                    ) : (
                      <img
                        src={transformToWebp(account.avatar)}
                        alt={account.nickname}
                      />
                    )}
                  </div>
                  <Dropdown
                    onVisibleChange={(visible) => {
                      setMenuVisible(visible);
                    }}
                    overlay={
                      <Menu>
                        {hasMerchantRole && (
                          <Menu.Item
                            key="merchant"
                            onClick={handleClickMerchant}
                          >
                            <span>{t('biz.user.menu.to.merchant')}</span>
                          </Menu.Item>
                        )}
                        {hasPartnerRole && (
                          <Menu.Item key="partner" onClick={handleClickPartner}>
                            <span>{t('biz.user.menu.to.partner')}</span>
                          </Menu.Item>
                        )}
                        {hasBankerRole && (
                          <Menu.Item key="banker" onClick={handleClickBanker}>
                            <span>{t('biz.user.menu.to.banker')}</span>
                          </Menu.Item>
                        )}
                        <Menu.Item key="logout" onClick={handleClickLogout}>
                          <span>{t('shared.logout')}</span>
                        </Menu.Item>
                      </Menu>
                    }
                    placement="bottom"
                  >
                    <div
                      className={classNames(css.nickname, {
                        [css.hover]: menuVisible,
                      })}
                    >
                      {account.nickname || account.email}
                      <span className={css.icon}>
                        {loggingOut || changing ? (
                          <LoadingOutlined />
                        ) : (
                          <CaretDownFilled className={css.caret} />
                        )}
                      </span>
                    </div>
                  </Dropdown>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button
                className={css.signUp}
                brand
                type="primary"
                href="/join_us"
                onClick={(e) => {
                  e.preventDefault();
                  history.push('/join_us');
                }}
              >
                {t('shared.joinNow')}
              </Button>
              <Button
                type="primary"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  history.push('/');
                }}
              >
                {t('shared.signIn')}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
